import { useEffect, useRef, useState } from 'react';

const SCROLL_DELTA_THRESHOLD = 8;
const TOP_EPSILON = 12;

const SCROLL_PORT_SELECTORS = [
  '[data-modal-scroll-port]',
  '.support-open-disc-thread-modal-scroll',
  '.support-open-disc-home-main',
  '.posts-page-layout__center',
  '.notifications-page-shell-main',
];

function isScrollableElement(el) {
  if (!(el instanceof HTMLElement)) return false;
  const { overflowY } = window.getComputedStyle(el);
  const allowsScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
  return allowsScroll && el.scrollHeight > el.clientHeight + 1;
}

function getScrollableElements() {
  if (typeof document === 'undefined') return [];
  const seen = new Set();
  const elements = [];
  for (const selector of SCROLL_PORT_SELECTORS) {
    document.querySelectorAll(selector).forEach((node) => {
      if (node instanceof HTMLElement && !seen.has(node) && isScrollableElement(node)) {
        seen.add(node);
        elements.push(node);
      }
    });
  }
  return elements;
}

function getActiveScrollTop() {
  const scrollables = getScrollableElements();
  if (scrollables.length > 0) {
    return Math.max(...scrollables.map((el) => el.scrollTop));
  }
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
}

/**
 * Mobile posts chrome: scroll down → FAB-only create button; scroll up → bottom icon bar.
 * When pinBar is true (story/article/thread open), always returns 'bar'.
 * @returns {'bar' | 'fab'}
 */
export function usePostsMobileScrollChrome(enabled, resetKey = '', pinBar = false) {
  const [chromeMode, setChromeMode] = useState('bar');
  const lastScrollTopRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    if (!enabled || pinBar) return undefined;

    lastScrollTopRef.current = getActiveScrollTop();
    setChromeMode('bar');

    const onScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        const scrollTop = getActiveScrollTop();
        const delta = scrollTop - lastScrollTopRef.current;

        if (scrollTop <= TOP_EPSILON) {
          setChromeMode('bar');
        } else if (Math.abs(delta) >= SCROLL_DELTA_THRESHOLD) {
          setChromeMode(delta > 0 ? 'fab' : 'bar');
        }

        lastScrollTopRef.current = scrollTop;
        tickingRef.current = false;
      });
    };

    const boundElements = new Set();

    const bindScrollPorts = () => {
      window.addEventListener('scroll', onScroll, { passive: true, capture: true });
      getScrollableElements().forEach((el) => {
        if (boundElements.has(el)) return;
        boundElements.add(el);
        el.addEventListener('scroll', onScroll, { passive: true });
      });
    };

    const unbindScrollPorts = () => {
      window.removeEventListener('scroll', onScroll, { capture: true });
      boundElements.forEach((el) => el.removeEventListener('scroll', onScroll));
      boundElements.clear();
    };

    bindScrollPorts();

    const mo = new MutationObserver(() => {
      unbindScrollPorts();
      bindScrollPorts();
      lastScrollTopRef.current = getActiveScrollTop();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      unbindScrollPorts();
      mo.disconnect();
    };
  }, [enabled, resetKey, pinBar]);

  useEffect(() => {
    if (!enabled) return;
    if (pinBar) setChromeMode('bar');
  }, [enabled, pinBar, resetKey]);

  if (pinBar && enabled) return 'bar';
  return chromeMode;
}
